import { LocationStrategy } from '@angular/common';
import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Api, ApiClient, httpStatus } from '../../api';
import { CONSTANTS_ERRORS } from '../../common/constants/errors/errors.constants';
import { CONSTANTS_ROUTING } from '../../common/constants/routing/routing.constants';
import { IWheelOfFortune, IWheelOfFortuneConfig } from './interfaces/wheel-of-fortune.interface';
import { WheelOfFortuneStates } from './wheel-of-fortune.states';

const MISSING_SURVEY_TOKEN = 'No survey token to verify a phone number for';

enum AGE_GROUP {
  LESS_THAN_13 = '<=12',
  LESS_THAN_18 = '13-17',
}

@Service()
export class WheelOfFortuneService {
  private api = inject(ApiClient);
  private locationStrategy = inject(LocationStrategy);
  private router = inject(Router);

  isWheelEnabled = false;
  prizeText: string | null = null;
  isWheelBlocked = true;
  venueUuid: string | null = null;
  campaignResponseUuid: string | null = null;
  campaignUuid: string | null = null;
  private readonly wheelOfFortuneState$ = new BehaviorSubject<WheelOfFortuneStates>(
    WheelOfFortuneStates.inProgress
  );
  private wheelBody: IWheelOfFortune | null = null;
  private wheelConfig: IWheelOfFortuneConfig | null = null;
  private winnerEmail: string | undefined;
  private surveyToken: string | null = null;
  private ageGroup: string | null = null;

  constructor() {
    this.preventBackButton();
  }

  fetchWheel$(id: string, visitorId: string): Observable<IWheelOfFortune> {
    return this.api
      .get(Api.surveys.takeaway.rewards.wheelSpin, {
        pathParams: { token: id },
        fingerprint: visitorId,
      })
      .pipe(
        catchError((error: unknown) => {
          if (httpStatus(error) === CONSTANTS_ERRORS.HTTP_500) {
            this.wheelOfFortuneState$.next(WheelOfFortuneStates.error);
          }
          return throwError(() => error);
        })
      );
  }

  fetchWheelCampaign$(
    campaignVenueId: string,
    responseId: string,
    visitorId: string
  ): Observable<IWheelOfFortune> {
    return this.api
      .get(Api.surveys.takeaway.campaign.rewards.wheelSpin, {
        pathParams: { venueUuid: campaignVenueId, uuid: responseId },
        fingerprint: visitorId,
      })
      .pipe(
        catchError((error: unknown) => {
          if (httpStatus(error) === CONSTANTS_ERRORS.HTTP_500) {
            this.wheelOfFortuneState$.next(WheelOfFortuneStates.error);
          }
          return throwError(() => error);
        })
      );
  }

  sendSMSCode$(
    phone: string,
    visitorId: string
  ): Observable<Api.surveys.takeaway.phone.sendVerificationCode.PostResult> {
    if (!this.surveyToken) {
      return throwError(() => new Error(MISSING_SURVEY_TOKEN));
    }

    return this.api.post(Api.surveys.takeaway.phone.sendVerificationCode, {
      pathParams: { token: this.surveyToken },
      fingerprint: visitorId,
      body: { phone },
    });
  }

  verifySMSCode$(
    code: string,
    visitorId: string
  ): Observable<Api.surveys.takeaway.phone.verify.PostResult> {
    if (!this.surveyToken) {
      return throwError(() => new Error(MISSING_SURVEY_TOKEN));
    }

    return this.api.post(Api.surveys.takeaway.phone.verify, {
      pathParams: { token: this.surveyToken },
      fingerprint: visitorId,
      body: { code },
    });
  }

  setWheelOfFortuneEnable(isEnable: boolean) {
    this.isWheelEnabled = isEnable;
  }

  setWheelOfFortuneBlocked(isBlocked: boolean) {
    this.isWheelBlocked = isBlocked;
  }

  setWheelBody(config: IWheelOfFortune | null) {
    this.wheelBody = config;
  }

  getWheelBody(): IWheelOfFortune | null {
    return this.wheelBody;
  }

  getWheelOfFortuneState$(): Observable<WheelOfFortuneStates> {
    return this.wheelOfFortuneState$.asObservable();
  }

  setWheelOfFortuneState(state: WheelOfFortuneStates) {
    this.wheelOfFortuneState$.next(state);
    window.scrollTo(0, 0);
  }

  setWinnerEmail(email: string) {
    this.winnerEmail = email;
  }

  getWinnerEmail(): string | undefined {
    return this.winnerEmail;
  }

  setSurveyToken(token: string) {
    this.surveyToken = token;
  }

  submitPrize$(visitorId: string): Observable<Api.surveys.takeaway.prizes.award.PatchResult> {
    const prizeUuid = this.wheelBody?.prize?.uuid;

    if (!prizeUuid || !this.winnerEmail) {
      this.setWheelOfFortuneState(WheelOfFortuneStates.error);

      return throwError(() => new Error('No prize or winner e-mail to award'));
    }

    return this.api
      .patch(Api.surveys.takeaway.prizes.award, {
        pathParams: { uuid: prizeUuid },
        fingerprint: visitorId,
        body: { email: this.winnerEmail },
      })
      .pipe(
        catchError((error: unknown) => {
          this.setWheelOfFortuneState(WheelOfFortuneStates.error);

          return throwError(() => error);
        })
      );
  }

  setWheelConfig(config: IWheelOfFortuneConfig | null): void {
    this.wheelConfig = config;
  }

  getWheelConfig(): IWheelOfFortuneConfig | null {
    return this.wheelConfig;
  }

  checkAndRedirectInCase(): void {
    if (!this.isWheelEnabled) {
      void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]);
    }
  }

  private preventBackButton() {
    this.locationStrategy.onPopState(() => {
      this.setWheelOfFortuneEnable(false);
    });
  }

  isAgeGroupAllowedForWheelSpin() {
    switch (this.wheelConfig?.ageLimit) {
      case 13:
        if (this.ageGroup === AGE_GROUP.LESS_THAN_13) {
          return false;
        }
        return true;

      case 18:
        if (this.ageGroup === AGE_GROUP.LESS_THAN_13) {
          return false;
        }
        if (this.ageGroup === AGE_GROUP.LESS_THAN_18) {
          return false;
        }
        return true;

      default:
        return true;
    }
  }

  setAgeGroup(ageGroup: string) {
    this.ageGroup = ageGroup;
  }
}
